import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getAuditLogs } from "../services/api";
import { setAlert } from "../store/slices/alertSlice";
import { subscribeToRealtimeEvent } from "../services/socket";

const actionOptions = [
  "",
  "LOGIN_SUCCESS",
  "LOGIN_FAILED",
  "LOGOUT",
  "PASSWORD_CHANGED",
  "USER_CREATED",
  "USER_UPDATED",
  "USER_DELETED",
];

const AuditLogs = () => {
  const dispatch = useDispatch();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    action: "",
    status: "",
  });

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAuditLogs({
        page,
        limit,
        action: filters.action || undefined,
        status: filters.status || undefined,
      });
      setLogs(data.logs || []);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      dispatch(setAlert({ type: "danger", message: "Error loading audit logs" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, filters.action, filters.status, limit, page]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    const unsubscribe = subscribeToRealtimeEvent("audit:changed", () => {
      loadLogs();
    });

    return () => unsubscribe?.();
  }, [loadLogs]);

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString();
  };

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h3>Audit Logs</h3>
          <p>Review important account and administration events.</p>
        </div>
      </div>

      <div className="dashboard-list-panel admin-grid-panel">
        <div className="dashboard-list-header">
          <div>
            <div className="admin-grid-title">Activity Log</div>
            <div className="list-count">{totalCount} records</div>
          </div>
          <div className="dashboard-table-tools audit-log-tools">
            <label>
              Action
              <select
                value={filters.action}
                onChange={(event) => updateFilter("action", event.target.value)}
              >
                {actionOptions.map((action) => (
                  <option key={action || "all"} value={action}>
                    {action || "All"}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select
                value={filters.status}
                onChange={(event) => updateFilter("status", event.target.value)}
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
              </select>
            </label>
            <label>
              Rows
              <select
                value={limit}
                onChange={(event) => {
                  setPage(1);
                  setLimit(Number(event.target.value));
                }}
              >
                {[25, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="grid-icon-btn"
              onClick={loadLogs}
              aria-label="Refresh audit logs"
              title="Refresh audit logs"
            >
              <i className="bi bi-arrow-repeat" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div className="modern-table-wrap">
          <table className="table modern-dashboard-table audit-log-table">
            <thead>
              <tr>
                <th>Sl No</th>
                <th>Date</th>
                <th>Action</th>
                <th>Status</th>
                <th>Actor</th>
                <th>Target</th>
                <th>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {logs.length ? logs.map((log, index) => (
                <tr key={log._id}>
                  <td>{(page - 1) * limit + index + 1}</td>
                  <td>{formatDate(log.createdAt)}</td>
                  <td><span className="audit-action-pill">{log.action}</span></td>
                  <td>
                    <span className={`audit-status-pill ${log.status}`}>
                      {log.status}
                    </span>
                  </td>
                  <td>
                    <div className="audit-main-text">{log.actorName || log.actorRole || "System"}</div>
                    {log.actorRole && <div className="audit-sub-text">{log.actorRole}</div>}
                  </td>
                  <td>
                    <div className="audit-main-text">{log.targetName || log.targetType || "-"}</div>
                    {log.targetType && <div className="audit-sub-text">{log.targetType}</div>}
                  </td>
                  <td>{log.ipAddress || "-"}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" className="text-center text-muted">
                    {loading ? "Loading..." : "No audit logs found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="dashboard-pagination">
          <div className="pagination-summary">
            Showing {totalCount === 0 ? 0 : ((page - 1) * limit) + 1}
            -{Math.min(page * limit, totalCount)} of {totalCount}
          </div>
          <div className="modern-pagination">
            <button type="button" disabled={page === 1} onClick={() => setPage(1)} title="First page">
              <i className="bi bi-chevron-double-left" aria-hidden="true"></i>
            </button>
            <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} title="Previous page">
              <i className="bi bi-chevron-left" aria-hidden="true"></i>
            </button>
            <button type="button" className="active">{page}</button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)} title="Next page">
              <i className="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Last page">
              <i className="bi bi-chevron-double-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
